import React, { useState, useEffect } from 'react';
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import { Badge } from 'src/components/invitation/badge';
import Button from 'src/components/Buttons/Button';
import { Spinner } from 'react-bootstrap';
import GanttChart from 'src/components/objectives/GanttChart';
import { getInformationUser, getObjectiveInfoRegister, getObjectivesFromBackend, saveObjectivesToBackend } from 'src/services/objectiveAIServices';
import './objectiveModal.css';

interface Objective {
  id: string;
  name: string;
  fullName: string;
  deadline: string;
  displayDate: string;
  priority: 'high' | 'medium';
  controls: string;
  evidence: string;
  risk: string;
  description: string;
  tooltip: string;
  weeksRemaining: number;
  inlineFine: string;
  startDate?: string;
  endDate?: string;
}

interface ApiObjective {
  objective_id: string;
  objective_name: string;
  description: string;
  estimated_effort: string;
  priority_rank: number;
  regulatory_reference: string;
  suggested_start_date: string;
  suggested_completion_date: string;
}

interface CompanyData {
  organization_name: string;
  headquarters_country: string;
  industry_sectors: string[];
  entity_type: string;
  key_services: string[];
  company_size: {
    employees: number | null;
    revenue_band: string;
  };
  applicable_compliance_frameworks?: Array<{
    id: string;
    name: string;
    description?: string;
  }>;
}

interface ApiResponse {
  success: boolean;
  data?: {
    roadmap?: ApiObjective[];
    data_sources?: string[];
    notes?: string;
  };
  message?: string;
}

const ObjectiveConfirmation = () => {
  const navigate = useNavigate();
  const [selectedObjectives, setSelectedObjectives] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isChartLoading, setIsChartLoading] = useState(true);
  const [objectives, setObjectives] = useState<Objective[]>([]);
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);

  // Format date for display
  const formatDisplayDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? 'TBD' : 
        date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return 'TBD';
    }
  };

  // Calculate weeks remaining until deadline
  const weeksToDeadline = (deadline: string) => {
    try {
      const today = new Date();
      const deadlineDate = new Date(deadline);
      const diffTime = deadlineDate.getTime() - today.getTime();
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 7));
    } catch {
      return 0;
    }
  };

  // Transform API objectives to our format
  const transformApiObjectives = (apiObjectives: ApiObjective[]): Objective[] => {
    if (!apiObjectives || !Array.isArray(apiObjectives)) return [];
    
    return apiObjectives.map(apiObj => {
      const priority = apiObj.priority_rank <= 2 ? 'high' : 'medium';
      const startDate = apiObj.suggested_start_date || '';
      const endDate = apiObj.suggested_completion_date || '';
      const weeksRemaining = weeksToDeadline(endDate);
      
      return {
        id: `obj-${apiObj.objective_id}`,
        name: (apiObj.objective_name || '').split(' ')[0] || 'Regulation',
        fullName: apiObj.objective_name || 'Regulatory Objective',
        deadline: endDate,
        displayDate: formatDisplayDate(endDate),
        priority,
        controls: 'TBD control statements',
        evidence: 'TBD evidence items',
        risk: apiObj.estimated_effort || 'Medium',
        description: apiObj.description || 'Compliance requirement',
        tooltip: apiObj.regulatory_reference || 'See official regulation',
        weeksRemaining: isNaN(weeksRemaining) ? 0 : Math.max(0, weeksRemaining),
        inlineFine: 'TBD',
        startDate,
        endDate
      };
    });
  };

  // Fetch company data and objectives
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setIsChartLoading(true);
      
      try {
        // 1. First, check backend for existing objectives
        const backendObjectives = await getObjectivesFromBackend();
        
        if (backendObjectives && Array.isArray(backendObjectives) && backendObjectives.length > 0) {
          // Use objectives from backend
          console.log("Objectives loaded from backend:", backendObjectives);
          
          // Extract roadmap from the first item (as per AI API response structure)
          const roadmap = backendObjectives[0]?.roadmap || [];
          
          if (roadmap.length > 0) {
            const transformedObjectives = transformApiObjectives(roadmap);
            setObjectives(transformedObjectives);
            setSelectedObjectives(transformedObjectives.map(obj => obj.id));
            toast.success("Objectives loaded from saved data");
            
            // Also set company data if available
            const companyInfo = await getInformationUser().catch(() => null);
            if (companyInfo) {
              setCompanyData(companyInfo);
            }
            
            setTimeout(() => {
              setIsChartLoading(false);
              setIsLoading(false);
            }, 500);
            return; // Exit early, no need to call AI API
          }
        }
        
        // 2. No objectives in backend, fetch from AI API
        console.log("No objectives found in backend, fetching from AI API...");
        
        // Get company information
        const companyInfo = await getInformationUser();
        setCompanyData(companyInfo);
        console.log("Company Info:", companyInfo);
        
        // Get objectives based on company data from AI API
        const response = await getObjectiveInfoRegister(companyInfo);
        
        console.log("AI API Response:", response);

        if (response.success && response.data?.roadmap) {
          const roadmap = response.data.roadmap;
          const transformedObjectives = transformApiObjectives(roadmap);
          
          setObjectives(transformedObjectives);
          setSelectedObjectives(transformedObjectives.map(obj => obj.id));
          
          // Save the AI response to backend
          // The AI API returns an array with one object containing roadmap, data_sources, and notes
          const aiResponseData = [{
            roadmap: response.data.roadmap,
            data_sources: response.data.data_sources || [],
            notes: response.data.notes || ""
          }];
          
          const saveResult = await saveObjectivesToBackend(aiResponseData);
          if (saveResult.success) {
            console.log("Objectives saved to backend successfully");
            toast.success("Objectives loaded and saved successfully");
          } else {
            console.warn("Failed to save objectives to backend:", saveResult.message);
            toast.warning("Objectives loaded but failed to save: " + (saveResult.message || "Unknown error"));
          }
          
          setTimeout(() => {
            setIsChartLoading(false);
            setIsLoading(false);
          }, 500);
        } else {
          toast.info(response.message || "No compliance objectives found for your organization.");
          setIsLoading(false);
          setIsChartLoading(false);
        }
      } catch (error) {
        console.error("Error loading objectives:", error);
        toast.error("Failed to load objectives");
        setIsLoading(false);
        setIsChartLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const handleObjectiveToggle = (objectiveId: string) => {
    setSelectedObjectives(prev => 
      prev.includes(objectiveId) 
        ? prev.filter(id => id !== objectiveId)
        : [...prev, objectiveId]
    );
  };

  const handleAcceptRoadmap = async () => {
   

      navigate('/frameWork');
  };

  if (isLoading) {
    return (
      <div className="root">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <h4 className="loading-title">Analyzing your organization</h4>
          <p className="loading-subtitle">We're identifying relevant compliance objectives</p>
        </div>
      </div>
    );
  }

  return (
    <div className="root">
      <main>
        <div className="mx-auto" style={{ maxWidth: '1200px' }}>
          <div className="roadmap-header">
            <h1 className="roadmap-title">Compliance Roadmap</h1>
            <p className="roadmap-subtitle">
              Based on your organization profile, these are the prioritized regulatory objectives
            </p>
          </div>

          {companyData?.applicable_compliance_frameworks && (
            <div className="frameworks-section">
              <h5 className="frameworks-title">Applicable Compliance Frameworks</h5>
              <div className="frameworks-container">
                {companyData.applicable_compliance_frameworks.map((framework, index) => (
                  <span key={index} className="framework-badge">
                    {framework.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="timeline-section">
            <h5 className="timeline-title">Implementation Timeline</h5>
            {isChartLoading ? (
              <div className="timeline-loading">
                <div className="timeline-spinner"></div>
                <p className="timeline-loading-text">Building timeline visualization...</p>
              </div>
            ) : (
              <GanttChart 
                objectives={objectives}
                selectedObjectives={selectedObjectives}
                onObjectiveToggle={handleObjectiveToggle}
              />
            )}
          </div>

          <div className="objectives-section">
            <h5 className="objectives-title">Detailed Objectives</h5>
            <div className="objectives-grid">
              {objectives.length > 0 ? (
                objectives.map((objective) => (
                  <div 
                    className={`objective-card ${selectedObjectives.includes(objective.id) ? 'selected' : ''}`}
                    key={objective.id}
                  >
                    <div className="objective-header">
                      <h5 className="objective-title">{objective.fullName}</h5>
                      <span className={`priority-badge ${objective.priority}`}>
                        {objective.priority}
                      </span>
                    </div>
                    <p className="objective-description">{objective.description}</p>
                    <div className="objective-meta">
                      <div className="meta-item">
                        <span className="meta-icon">📅</span>
                        <span>Deadline: {objective.displayDate}</span>
                      </div>
                      <div className="meta-item">
                        <span className="meta-icon">⚡</span>
                        <span>Risk Level: {objective.risk}</span>
                      </div>
                      <div className="meta-item">
                        <span className="meta-icon">📊</span>
                        <span>Weeks Remaining: {objective.weeksRemaining}</span>
                      </div>
                    </div>
                    <div className="objective-footer">
                      <div className="objective-reference" title={objective.tooltip}>
                        Reference: {objective.tooltip}
                      </div>
                      <button
                        className={`select-button ${selectedObjectives.includes(objective.id) ? 'selected' : ''}`}
                        onClick={() => handleObjectiveToggle(objective.id)}
                      >
                        {selectedObjectives.includes(objective.id) ? 'Selected' : 'Select'}
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  <div className="empty-icon">📋</div>
                  <h3 className="empty-title">No Objectives Available</h3>
                  <p className="empty-description">
                    No objectives available. Please check your organization's compliance requirements.
                  </p>
                </div>
              )}
            </div>
          </div>



          <div className="action-buttons">
            <button
              onClick={() => navigate('/frameWork')}
              className="build-roadmap-button"
            >
              Build Roadmap
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ObjectiveConfirmation;